import { z } from 'zod';

export const REGEX_PATTERN_MAX_LENGTH = 256;

const hasLikelyUnsafeRegexShape = (pattern: string): boolean => {
  const hasNestedQuantifiers = /\([^)]*[+*][^)]*\)[+*{]/.test(pattern);
  const hasRepeatedGreedyWildcards = /(\.\*){2,}/.test(pattern);
  const hasAlternationGroupWithQuantifier = /\([^)]*\|[^)]*\)[+*{]/.test(
    pattern
  );
  return (
    hasNestedQuantifiers ||
    hasRepeatedGreedyWildcards ||
    hasAlternationGroupWithQuantifier
  );
};

export const normalizeRegexPattern = (pattern: string): string => {
  return pattern.trim();
};

export const getRegexPatternValidationError = (
  pattern: unknown
): string | null => {
  if (typeof pattern !== 'string') {
    return 'Pattern must be a string.';
  }

  const normalizedPattern = normalizeRegexPattern(pattern);
  if (normalizedPattern.length === 0) {
    return 'Pattern cannot be blank.';
  }

  if (normalizedPattern.length > REGEX_PATTERN_MAX_LENGTH) {
    return `Pattern cannot exceed ${REGEX_PATTERN_MAX_LENGTH} characters.`;
  }

  if (hasLikelyUnsafeRegexShape(normalizedPattern)) {
    return 'Pattern appears unsafe and may cause excessive backtracking.';
  }

  try {
    new RegExp(normalizedPattern);
  } catch {
    return 'Pattern must be a valid regular expression.';
  }

  return null;
};

export const isSafeRegexPattern = (pattern: unknown): boolean => {
  return getRegexPatternValidationError(pattern) === null;
};

export const SafeRegexPatternSchema = z
  .string()
  .trim()
  .superRefine((pattern, context) => {
    const validationError = getRegexPatternValidationError(pattern);
    if (!validationError) {
      return;
    }

    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: validationError,
    });
  });

export const SafeRegexPatternArraySchema = z.array(SafeRegexPatternSchema);
